import { Config } from '@/config';
import { getSupportedLocales } from '@/plugins/i18n';
import apiService from '@/services/api.service';
import { Locales } from '@/store/language.interface';
import { User, UserDto, useUserStore } from '@/store/userStore';

// A language picked while logged out, kept for the tab's round trip to the login page.
// sessionStorage, so it never reaches whoever logs in next on a shared computer.
const pendingLanguageKey = 'pendingLanguage';

function forgetPendingLanguage(): void {
  try {
    sessionStorage.removeItem(pendingLanguageKey);
  } catch {
    // Storage unavailable: nothing was kept.
  }
}

class UserService {
  async updateUser(user: User) {
    const userStore = useUserStore();
    try {
      const response = await apiService.put<UserDto>(`${Config.api.backend.urlV2}/user`, user);
      // The web app's PUT resolves with `undefined` on a 4xx or 5xx, such as the 422 for a birth year out of range
      // (COCO-3009). Callers read `success` to show their save error and release their Save button.
      if (!response?.success) {
        return { success: false };
      }
      if ('emailConsent' in user) {
        response.data.emailConsent = user.emailConsent ? 1 : 0;
      }
      userStore.setUser(response.data);
      return response;
    } catch {
      return { success: false };
    }
  }

  // The account language decides the language of every email the user receives.
  async recordLanguageChoice(locale: Locales): Promise<void> {
    if (useUserStore().isUserLogged) {
      // A newer pick than any still waiting from before the login.
      if (await this.saveLanguage(locale)) forgetPendingLanguage();
      return;
    }
    try {
      sessionStorage.setItem(pendingLanguageKey, locale);
    } catch {
      // Storage unavailable: the choice still applies to this visit.
    }
  }

  // Forgotten only once saved, so a failed save is tried again at the next login in this tab.
  async savePendingLanguageChoice(): Promise<void> {
    if (!useUserStore().isUserLogged) return;
    let locale: string | null = null;
    try {
      locale = sessionStorage.getItem(pendingLanguageKey);
    } catch {
      return;
    }
    if (!getSupportedLocales().includes(locale as Locales)) return;
    if (await this.saveLanguage(locale as Locales)) forgetPendingLanguage();
  }

  private async saveLanguage(locale: Locales): Promise<boolean> {
    if (locale === useUserStore().user.preferredLanguage) return true;
    try {
      const response = await apiService.put(`${Config.api.backend.urlV2}/user`, { language_code: locale });
      // Resolves with `undefined` on a 4xx or 5xx (COCO-3009): the account kept its language.
      if (!response?.success) return false;
      useUserStore().setPreferredLanguage(locale);
      return true;
    } catch {
      // apiService already logged it; the page language has changed either way.
      return false;
    }
  }
}

export const userService = new UserService();
